import { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useNavigate } from "react-router-dom";
import {
  Check,
  ChevronDown,
  CircleCheck,
  CircleX,
  Info,
  Megaphone,
  TriangleAlert,
} from "lucide-react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

const ALERT_TYPES = [
  { value: "info", label: "Info", Icon: Info, hex: "#2563eb", color: "text-blue-600", bg: "bg-blue-50" },
  { value: "success", label: "Success", Icon: CircleCheck, hex: "#16a34a", color: "text-green-600", bg: "bg-green-50" },
  { value: "warning", label: "Warning", Icon: TriangleAlert, hex: "#f59e0b", color: "text-amber-500", bg: "bg-amber-50" },
  { value: "error", label: "Error", Icon: CircleX, hex: "#dc2626", color: "text-red-600", bg: "bg-red-50" },
  { value: "announcement", label: "Announcement", Icon: Megaphone, hex: "#9333ea", color: "text-purple-600", bg: "bg-purple-50" },
];

// SVG markup of the selected icon, stored so the app can render it as-is.
const buildAlertIconSvg = ({ Icon, hex }) =>
  renderToStaticMarkup(<Icon color={hex} size={24} />);

export default function AppSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const typeRef = useRef(null);
  const [form, setForm] = useState({
    enable_flight_alert: false,
    flight_alert_type: "info",
    flight_alert_message_content: "",
    enable_maintenance_mode: false,
  });

  useEffect(() => {
    if (!typeOpen) return;
    const onClickOutside = (e) => {
      if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [typeOpen]);

  const selectedType =
    ALERT_TYPES.find((t) => t.value === form.flight_alert_type) || ALERT_TYPES[0];

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/app/settings");
      if (res.data?.data) {
        const d = res.data.data;
        setForm({
          enable_flight_alert: !!d.enable_flight_alert,
          flight_alert_type: d.flight_alert_type || "info",
          flight_alert_message_content: d.flight_alert_message_content || "",
          enable_maintenance_mode: !!d.enable_maintenance_mode,
        });
      }
    } catch (e) {
      toast.error("Failed to load app settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const onSubmit = async () => {
    if (form.enable_flight_alert && !form.flight_alert_message_content.trim()) {
      toast.error("Flight alert message content is required when flight alert is enabled.");
      return;
    }
    try {
      setSaving(true);
      await api.post("/admin/app/settings", {
        ...form,
        flight_alert_icon: buildAlertIconSvg(selectedType),
      });
      toast.success("App settings updated successfully!");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm">Loading...</div>;

  return (
    <div className="bg-white rounded-2xl p-6">
      <button
        className="text-sm text-blue-600 font-semibold mb-4"
        onClick={() => navigate("/admin/settings-management")}
      >
        ← Back to Settings
      </button>

      <h1 className="text-2xl font-bold">Site / APP Settings</h1>
      <p className="text-sm text-gray-500 mt-2">
        Configure site and application-level settings.
      </p>

      <div className="mt-8 space-y-6">
        {/* Enable Flight Alert toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-xl px-5 py-4">
          <div>
            <p className="text-base font-semibold">Enable Flight Alert</p>
            <p className="text-sm text-gray-500 mt-0.5">
              Allow users to receive flight price and status alerts.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm font-semibold ${
                form.enable_flight_alert ? "text-green-600" : "text-red-500"
              }`}
            >
              {form.enable_flight_alert ? "Enabled" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  enable_flight_alert: !prev.enable_flight_alert,
                }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                form.enable_flight_alert ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  form.enable_flight_alert ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Flight Alert Type */}
        {form.enable_flight_alert && (
          <div>
            <label className="text-base font-semibold block mb-2">
              Flight Alert Type
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Choose the icon and style used when the alert is shown to users.
            </p>
            <div ref={typeRef} className="relative w-full sm:w-80">
              <button
                type="button"
                onClick={() => setTypeOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 border rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black bg-white"
              >
                <span className="flex items-center gap-3">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${selectedType.bg}`}>
                    <selectedType.Icon className={`h-4 w-4 ${selectedType.color}`} />
                  </span>
                  <span className="font-medium">{selectedType.label}</span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                    typeOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {typeOpen && (
                <ul className="absolute z-20 mt-2 w-full bg-white border rounded-xl shadow-lg py-1">
                  {ALERT_TYPES.map(({ value, label, Icon, color, bg }) => (
                    <li key={value}>
                      <button
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, flight_alert_type: value }));
                          setTypeOpen(false);
                        }}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 ${
                          form.flight_alert_type === value ? "bg-gray-50" : ""
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${bg}`}>
                            <Icon className={`h-4 w-4 ${color}`} />
                          </span>
                          <span className="font-medium">{label}</span>
                        </span>
                        {form.flight_alert_type === value && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Flight Alert Message Content */}
        {form.enable_flight_alert && (
          <div>
            <label className="text-base font-semibold block mb-2">
              Flight Alert Message Content{" "}
              <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-3">
              This message will be shown to users alongside flight alerts.
            </p>
            <textarea
              rows={4}
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-black resize-none text-sm"
              placeholder="Enter the flight alert message content..."
              value={form.flight_alert_message_content}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  flight_alert_message_content: e.target.value,
                }))
              }
            />
          </div>
        )}

        {/* Enable Maintenance Mode toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-xl px-5 py-4">
          <div>
            <p className="text-base font-semibold">Enable Maintenance Mode</p>
            <p className="text-sm text-gray-500 mt-0.5">
              When enabled, users will see a maintenance screen and normal app usage will be blocked.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm font-semibold ${
                form.enable_maintenance_mode ? "text-green-600" : "text-red-500"
              }`}
            >
              {form.enable_maintenance_mode ? "Enabled" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  enable_maintenance_mode: !prev.enable_maintenance_mode,
                }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                form.enable_maintenance_mode ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  form.enable_maintenance_mode ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Updating..." : "Update Settings"}
        </button>
      </div>
    </div>
  );
}
